import React from 'react';
import { Link } from 'react-router-dom';
import { FooterSection } from '../components/landing';
import { useSEO } from '../hooks/useSEO';

const AboutPage: React.FC = () => {
  useSEO({
    title: 'About Retrosive — Built for Agile Teams',
    description:
      'Learn about Retrosive — our mission, values, and the team behind the modern retrospective tool trusted by agile teams worldwide.',
    canonical: '/about',
  });
  const values = [
    {
      icon: '🎯',
      title: 'Simplicity First',
      description: 'We believe great tools should be intuitive. No steep learning curves, just effective retrospectives.',
    },
    {
      icon: '🤝',
      title: 'Team Collaboration',
      description: 'Built for teams, by teams. Every feature is designed to enhance how teams work together.',
    },
    {
      icon: '⚡',
      title: 'Real-time Everything',
      description: 'No refresh buttons, no waiting. See changes instantly as your team collaborates.',
    },
    {
      icon: '🔐',
      title: 'Privacy Matters',
      description: 'Your retrospective data is yours. We take security and privacy seriously.',
    },
    {
      icon: '🌍',
      title: 'Remote-First',
      description: 'Designed specifically for distributed teams working across time zones.',
    },
    {
      icon: '🚀',
      title: 'Continuous Improvement',
      description: 'Just like agile teams, we continuously improve Retrosive based on your feedback.',
    },
  ];

  const stats = [
    { value: '12+', label: 'Templates' },
    { value: 'Real-time', label: 'Collaboration' },
    { value: 'Free', label: 'To Start' },
    { value: '24/7', label: 'Availability' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-orange-800 to-slate-900 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-semibold mb-6 backdrop-blur-sm border border-white/20">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              We're on a mission to make
              <span className="block bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                retrospectives better
              </span>
            </h1>
            <p className="text-xl text-gray-300">
              Retrosive was born from a simple observation: retrospectives are crucial for team 
              improvement, but existing tools make them feel like a chore. We set out to change that.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600 text-lg">
                <p>
                  As software developers and team leaders ourselves, we've sat through 
                  countless retrospectives. Some were great, many were... not so great.
                </p>
                <p>
                  We noticed a pattern: when teams had the right tools and environment, 
                  retrospectives became energizing sessions that drove real improvement. 
                  But too often, clunky tools and boring formats turned them into 
                  something teams dreaded.
                </p>
                <p>
                  That's why we built Retrosive. We wanted to create a tool that makes 
                  retrospectives something teams actually look forward to. With real-time 
                  collaboration, beautiful templates, and thoughtful features like private 
                  writing mode, we help teams have more honest, productive conversations.
                </p>
                <p>
                  Today, teams around the world use Retrosive to continuously improve. And
                  we're just getting started.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl p-8 lg:p-12">
              <div className="grid grid-cols-2 gap-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-4xl font-bold bg-gradient-to-r from-orange-500 to-amber-600 bg-clip-text text-transparent mb-2">
                      {stat.value}
                    </div>
                    <div className="text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do at Retrosive.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <span className="text-4xl mb-4 block">{value.icon}</span>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-orange-500 to-amber-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Start using Retrosive today
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Start running better retrospectives today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="px-8 py-4 bg-white text-orange-500 font-bold text-lg rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Get Started Free
            </Link>
            <Link
              to="/features"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white font-bold text-lg rounded-xl hover:bg-white/20 transition-all duration-300"
            >
              Explore Features
            </Link>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default AboutPage;

